import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth-context.js';
import { useOrg } from '../lib/org-context.js';
import { apiFetch } from '../lib/api.js';

interface SupportContext {
  org: {
    id: string;
    name: string;
    status: string;
    subscription_status: string | null;
    trial_ends_at: string | null;
    created_at: string;
    settings: Record<string, unknown> | null;
  };
  last_lead_at: string | null;
  dead_letter_count: number;
  last_admin_login_at: string | null;
  notification_fail_count_7d: number;
  active_drip_sequence_count: number;
}

export function SupportContextPanel() {
  const { isImpersonating } = useAuth();
  const { viewingOrgId } = useOrg();
  const [data, setData] = useState<SupportContext | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchContext = useCallback(async () => {
    if (!viewingOrgId) return;
    setIsLoading(true);
    try {
      const d = await apiFetch<SupportContext>(`/admin/orgs/${viewingOrgId}/context`);
      setData(d);
    } catch { /* best-effort */ }
    finally { setIsLoading(false); }
  }, [viewingOrgId]);

  useEffect(() => {
    if (isImpersonating && viewingOrgId) fetchContext();
  }, [isImpersonating, viewingOrgId, fetchContext]);

  if (!isImpersonating || !viewingOrgId) return null;

  const relTime = (iso: string | null) => {
    if (!iso) return 'Never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-1 text-xs text-amber-700 hover:bg-amber-100 transition-colors flex items-center justify-center gap-1"
      >
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        Support Context
      </button>

      {isOpen && (
        <div className="px-4 pb-3 pt-1">
          {isLoading ? (
            <div className="flex gap-3">{[1,2,3,4].map(i => <div key={i} className="h-12 flex-1 bg-amber-100 rounded-lg animate-pulse" />)}</div>
          ) : data ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
              <ContextStat
                label="Plan"
                value={data.org.subscription_status ?? 'none'}
                warn={data.org.subscription_status === 'past_due'}
              />
              <ContextStat
                label="Trial Ends"
                value={data.org.trial_ends_at ? new Date(data.org.trial_ends_at).toLocaleDateString() : 'N/A'}
                warn={data.org.trial_ends_at ? new Date(data.org.trial_ends_at).getTime() < Date.now() : false}
              />
              <ContextStat
                label="Last Lead"
                value={relTime(data.last_lead_at)}
                warn={!data.last_lead_at || (Date.now() - new Date(data.last_lead_at).getTime()) > 30 * 86400_000}
              />
              <ContextStat
                label="Dead Letters"
                value={String(data.dead_letter_count)}
                warn={data.dead_letter_count > 0}
              />
              <ContextStat
                label="Notif Fails (7d)"
                value={String(data.notification_fail_count_7d)}
                warn={data.notification_fail_count_7d > 0}
              />
              <ContextStat
                label="Last Login"
                value={relTime(data.last_admin_login_at)}
                warn={!data.last_admin_login_at || (Date.now() - new Date(data.last_admin_login_at).getTime()) > 7 * 86400_000}
              />
            </div>
          ) : (
            <p className="text-xs text-amber-600">Failed to load context</p>
          )}
        </div>
      )}
    </div>
  );
}

function ContextStat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 ${warn ? 'bg-red-50 border border-red-200' : 'bg-white border border-amber-200'}`}>
      <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-xs font-medium ${warn ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}
